import React, { useState, useEffect, useRef } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "./PdfViewer.css";

pdfjs.GlobalWorkerOptions.workerSrc = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.12.313/pdf.worker.min.js";

export default function PdfViewer({
  url,
  readOnly = false,
  showThumbnails = true
}) {
  const [numPages, setNumPages] = useState(null);
  const [scale, setScale] = useState(1.2);
  const [rotation, setRotation] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageInput, setPageInput] = useState("1");
  const [sidebarVisible, setSidebarVisible] = useState(showThumbnails);
  const [pagesReady, setPagesReady] = useState(false);

  const viewerRef = useRef(null);
  const sidebarRef = useRef(null);
  const scrollTimeout = useRef(null);
  const isManualScroll = useRef(false);
  const pageRefs = useRef({});
  const thumbRefs = useRef({});
  const toolbarRef = useRef(null);

  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages);

    pageRefs.current = {};
    thumbRefs.current = {};

    setTimeout(() => {
      setPagesReady(true);
    }, 100);
  }

  const zoomIn = () => setScale(s => Math.min(3, s + 0.2));
  const zoomOut = () => setScale(s => Math.max(0.5, s - 0.2));
  const rotate = () => setRotation(r => (r + 90) % 360);

  const scrollToPage = (page) => {
    const el = pageRefs.current[page];
    const container = viewerRef.current;
    const toolbarHeight = toolbarRef.current && toolbarRef.current.offsetHeight || 0;

    if (!el || !container) return;

    isManualScroll.current = true;

    const containerRect = container.getBoundingClientRect();
    const pageRect = el.getBoundingClientRect();

    const scrollOffset =
      pageRect.top - containerRect.top + container.scrollTop - toolbarHeight - 10;

    container.scrollTo({
      top: Math.max(scrollOffset, 0),
      behavior: "smooth"
    });

    setTimeout(() => {
      isManualScroll.current = false;
    }, 400);
  };

  const nextPage = () => {
    if (currentPage < numPages) scrollToPage(currentPage + 1);
  };

  const prevPage = () => {
    if (currentPage > 1) scrollToPage(currentPage - 1);
  };

  /* Detect visible page while scrolling */
  useEffect(() => {
    const container = viewerRef.current;
    if (!container || !pagesReady) return;

    const detectPage = () => {
      if (isManualScroll.current) return;

      const scrollTop = container.scrollTop + container.clientHeight * 0.25;

      let closestPage = 1;
      let minDistance = Infinity;

      Object.entries(pageRefs.current).forEach(([page, el]) => {
        if (!el) return;

        const offset = el.offsetTop;
        const distance = Math.abs(offset - scrollTop);

        if (distance < minDistance) {
          minDistance = distance;
          closestPage = Number(page);
        }
      });

      setCurrentPage((prev) => {
        if (prev === closestPage) return prev;
        return closestPage;
      });
    };

    const handleScroll = () => {
      clearTimeout(scrollTimeout.current);

      scrollTimeout.current = setTimeout(() => {
        detectPage();
      }, 80); // debounce
    };

    container.addEventListener("scroll", handleScroll);

    return () => {
      container.removeEventListener("scroll", handleScroll);
      clearTimeout(scrollTimeout.current);
    };
  }, [pagesReady]);

  /* Auto-scroll thumbnails */
  useEffect(() => {
    const sidebar = sidebarRef.current;
    const thumb = thumbRefs.current[currentPage];

    if (!sidebar || !thumb) return;

    const thumbRect = thumb.getBoundingClientRect();
    const sidebarRect = sidebar.getBoundingClientRect();

    if (
      thumbRect.top < sidebarRect.top ||
      thumbRect.bottom > sidebarRect.bottom
    ) {
      thumb.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
    }
  }, [currentPage]);

  /* Disable right-click + print shortcuts in readOnly mode */
  useEffect(() => {
    if (!readOnly) return;

    const disableContext = (e) => e.preventDefault();

    const disableKeys = (e) => {
      if (
        (e.ctrlKey && ["p", "s"].includes(e.key.toLowerCase())) ||
        e.key === "PrintScreen"
      ) {
        e.preventDefault();
      }
    };

    document.addEventListener("contextmenu", disableContext);
    window.addEventListener("keydown", disableKeys);

    return () => {
      document.removeEventListener("contextmenu", disableContext);
      window.removeEventListener("keydown", disableKeys);
    };
  }, [readOnly]);

  useEffect(() => {
    setPageInput(String(currentPage));
  }, [currentPage]);

  return (
    <Document
      file={url}
      onLoadSuccess={onDocumentLoadSuccess}
      loading="Loading PDF..."
    >
      <div className="pdf-viewer-container">

        {/* Sidebar thumbnails */}
        {sidebarVisible && (
          <div ref={sidebarRef} className="pdf-sidebar">
            {numPages &&
              Array.from(new Array(numPages), (_, index) => {
                const page = index + 1;

                return (
                  <div
                    key={page}
                    ref={(el) => (thumbRefs.current[page] = el)}
                    onClick={() => scrollToPage(page)}
                    className={`pdf-thumbnail ${currentPage === page ? 'active' : ''}`}
                  >
                    <Page
                      pageNumber={page}
                      width={110}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />
                  </div>
                );
              })}
          </div>
        )}

        {/* Main viewer */}
        <div className="pdf-main-content">

          {/* Toolbar */}
          <div ref={toolbarRef} className="pdf-toolbar">

            {showThumbnails && (
              <button
                className="pdf-button"
                onClick={() => setSidebarVisible(v => !v)}
              >
                ☰ Pages
              </button>
            )}

            <button className="pdf-button" onClick={prevPage}>◀</button>
            <button className="pdf-button" onClick={nextPage}>▶</button>

            <button className="pdf-button" onClick={zoomOut}>−</button>
            <button className="pdf-button" onClick={zoomIn}>+</button>

            <button className="pdf-button" onClick={rotate}>⟳</button>

            <span className="pdf-toolbar-section">
              Page
              <input
                type="text"
                value={pageInput}
                onChange={(e) => {
                  setPageInput(e.target.value);
                }}
                onBlur={() => {
                  const page = Number(pageInput);

                  if (!page || page < 1 || page > numPages) {
                    setPageInput(String(currentPage));
                    return;
                  }

                  scrollToPage(page);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    const page = Number(pageInput);

                    if (!page || page < 1 || page > numPages) {
                      setPageInput(String(currentPage));
                      return;
                    }

                    scrollToPage(page);
                  }
                }}
                className="pdf-page-input"
              />
              / {numPages || 0}
            </span>

          </div>
          <div ref={viewerRef} className="pdf-viewer">
            {/* PDF pages */}
            {numPages &&
              Array.from(new Array(numPages), (_, index) => {
                const page = index + 1;

                return (
                  <div
                    key={page}
                    data-page={page}
                    ref={(el) => (pageRefs.current[page] = el)}
                    className="pdf-page-wrapper"
                  >
                    <Page
                      pageNumber={page}
                      scale={scale}
                      rotate={rotation}
                    />
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    </Document>
  );
}